import React, { useCallback, useEffect, useState } from "react";
import { Edit3, Loader2, PackagePlus, PlusCircle, RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import { AdminPageHero, adminHeroActionClassName } from "../../modules/admin/ui";

const CATEGORY_OPTIONS = [
  ["CONSUMABLE", "Consommable"],
  ["CONTAINER", "Bac / conteneur"],
  ["ROLLING_EQUIPMENT", "Matériel roulant"],
  ["FIELD_EQUIPMENT", "Équipement terrain"],
  ["TOOL", "Outil"],
  ["PPE", "EPI"],
] as const;

const FAMILY_OPTIONS = [
  "BAC",
  "POUBELLE",
  "CONTENEUR",
  "SAC",
  "TRICYCLE",
  "MOTO",
  "CAMION",
  "PICKUP",
  "REMORQUE",
  "OUTIL",
  "EPI",
  "MACHINE",
  "AUTRE",
] as const;

const NATURE_OPTIONS = [
  ["CONSUMABLE", "Consommable"],
  ["DURABLE", "Durable"],
] as const;

type CatalogRow = {
  id: number;
  code: string;
  displayName: string;
  category: string;
  family: string | null;
  nature: string;
  unit: string;
  volumeLiters: number | null;
  material: string | null;
  color: string | null;
  model: string | null;
  brand: string | null;
  capacityKg: number | null;
  capacityM3: number | null;
  fuelType: string | null;
  requiresPlate: boolean;
  isDotationEligible: boolean;
  alertThreshold: number | null;
  isActive: boolean;
  stock?: { calculated: number; reserved: number; available: number };
};

function apiErr(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { error?: { message?: string } | string } } };
  const e = ax.response?.data?.error;
  if (typeof e === "string") return e;
  if (e && typeof e === "object" && "message" in e) return String(e.message);
  return fallback;
}

const emptyForm = {
  code: "",
  category: "CONTAINER",
  family: "BAC",
  nature: "DURABLE",
  displayName: "",
  volumeLiters: "",
  material: "",
  color: "",
  model: "",
  brand: "",
  capacityKg: "",
  capacityM3: "",
  fuelType: "",
  requiresPlate: false,
  isDotationEligible: false,
  unit: "UNITE",
  alertThreshold: "",
  isActive: true,
};

function categoryLabel(category: string): string {
  return CATEGORY_OPTIONS.find(([value]) => value === category)?.[1] ?? category;
}

function defaultFamily(category: string): string {
  if (category === "CONSUMABLE") return "SAC";
  if (category === "ROLLING_EQUIPMENT") return "TRICYCLE";
  if (category === "PPE") return "EPI";
  if (category === "TOOL") return "OUTIL";
  return "BAC";
}

function defaultNature(family: string, category: string): string {
  if (family === "SAC" || category === "CONSUMABLE") return "CONSUMABLE";
  return "DURABLE";
}

const ResourceCatalogPage: React.FC = () => {
  const [rows, setRows] = useState<CatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/resources/catalogue");
      setRows((res.data?.data ?? []) as CatalogRow[]);
    } catch (err) {
      setRows([]);
      toast.error(apiErr(err, "Impossible de charger le catalogue."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.displayName.trim() || !form.family.trim() || !form.category.trim() || !form.nature.trim()) {
      toast.error("Catégorie, famille, nom affiché et nature obligatoires.");
      return;
    }
    try {
      const payload = {
        code: form.code.trim() || null,
        displayName: form.displayName.trim(),
        category: form.category,
        family: form.family,
        nature: form.nature,
        volumeLiters: form.volumeLiters ? Number(form.volumeLiters) : null,
        material: form.material.trim() || null,
        color: form.color.trim() || null,
        model: form.model.trim() || null,
        brand: form.brand.trim() || null,
        capacityKg: form.capacityKg ? Number(form.capacityKg) : null,
        capacityM3: form.capacityM3 ? Number(form.capacityM3) : null,
        fuelType: form.fuelType.trim() || null,
        requiresPlate: form.category === "ROLLING_EQUIPMENT" ? true : form.requiresPlate,
        isDotationEligible: form.category === "ROLLING_EQUIPMENT" ? false : form.isDotationEligible,
        unit: form.unit.trim() || "UNITE",
        alertThreshold: form.alertThreshold ? Number(form.alertThreshold) : null,
        isActive: form.isActive,
      };
      if (editingId) {
        await api.put(`/resources/catalogue/${editingId}`, payload);
        toast.success("Article catalogue modifié.");
      } else {
        await api.post("/resources/catalogue", payload);
        toast.success("Article catalogue créé.");
      }
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(apiErr(err, "Création impossible."));
    }
  };

  const startEdit = (row: CatalogRow) => {
    setEditingId(row.id);
    setForm({
      code: row.code,
      category: row.category,
      family: row.family ?? defaultFamily(row.category),
      nature: row.nature,
      displayName: row.displayName,
      volumeLiters: row.volumeLiters == null ? "" : String(row.volumeLiters),
      material: row.material ?? "",
      color: row.color ?? "",
      model: row.model ?? "",
      brand: row.brand ?? "",
      capacityKg: row.capacityKg == null ? "" : String(row.capacityKg),
      capacityM3: row.capacityM3 == null ? "" : String(row.capacityM3),
      fuelType: row.fuelType ?? "",
      requiresPlate: row.requiresPlate,
      isDotationEligible: row.isDotationEligible,
      unit: row.unit,
      alertThreshold: row.alertThreshold == null ? "" : String(row.alertThreshold),
      isActive: row.isActive,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deactivate = async (id: number) => {
    if (!window.confirm("Désactiver ce produit catalogue ?")) return;
    try {
      await api.patch(`/resources/catalogue/${id}/deactivate`);
      toast.success("Produit désactivé.");
      await load();
    } catch (err) {
      toast.error(apiErr(err, "Désactivation impossible."));
    }
  };

  const remove = async (row: CatalogRow) => {
    if (!window.confirm(`Supprimer définitivement l’article « ${row.displayName} » ?`)) return;
    try {
      await api.delete(`/resources/catalogue/${row.id}`);
      toast.success("Article catalogue supprimé.");
      await load();
    } catch (err) {
      toast.error(apiErr(err, "Suppression impossible. Désactivez l’article s’il a déjà un historique."));
    }
  };

  return (
    <div className="space-y-5 text-slate-100">
      <AdminPageHero
        eyebrow="Terrain"
        title="Catalogue ressources"
        subtitle="Le catalogue définit les articles et modèles. Le stock réel vient uniquement des mouvements."
        actions={
          <button
            type="button"
            onClick={() => void load()}
            className={adminHeroActionClassName(false)}
          >
            <RefreshCcw className="h-4 w-4" />
            Actualiser
          </button>
        }
      />

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
          {editingId ? "Modifier article catalogue" : "Nouvel article catalogue"}
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Aucun stock réel n’est créé ici. Les quantités se font ensuite via Entrée stock.
        </p>
        <form onSubmit={(e) => void submit(e)} className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="text-xs text-slate-400">
            Catégorie *
            <select
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={form.category}
              onChange={(e) => {
                const category = e.target.value;
                const family = defaultFamily(category);
                setForm((f) => ({
                  ...f,
                  category,
                  family,
                  nature: defaultNature(family, category),
                  requiresPlate: category === "ROLLING_EQUIPMENT",
                  isDotationEligible: category === "ROLLING_EQUIPMENT" ? false : f.isDotationEligible,
                  unit: category === "CONSUMABLE" ? "LOT" : "UNITE",
                }));
              }}
            >
              {CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-400">
            Famille *
            <select
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={form.family}
              onChange={(e) => {
                const family = e.target.value;
                setForm((f) => ({ ...f, family, nature: defaultNature(family, f.category) }));
              }}
            >
              {FAMILY_OPTIONS.map((family) => <option key={family} value={family}>{family}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-400">
            Nature *
            <select
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={form.nature}
              onChange={(e) => setForm((f) => ({ ...f, nature: e.target.value }))}
            >
              {NATURE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <span className="mt-1 block text-[10px] text-slate-500">
              {form.nature === "CONSUMABLE"
                ? "Le stock est géré en quantité. Exemple : sacs, bacs donnés définitivement, poubelles consommées en dotation."
                : "La ressource peut être suivie individuellement. Exemple : bac récupérable, conteneur, tricycle, moto."}
            </span>
          </label>
          <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={form.category === "ROLLING_EQUIPMENT" ? false : form.isDotationEligible}
              disabled={form.category === "ROLLING_EQUIPMENT"}
              onChange={(e) => setForm((f) => ({ ...f, isDotationEligible: e.target.checked }))}
            />
            Éligible dotation client
            {form.category === "ROLLING_EQUIPMENT" ? (
              <span className="text-[10px] text-amber-300">Matériel roulant exclu</span>
            ) : null}
          </label>
          <label className="text-xs text-slate-400 md:col-span-2">
            Nom affiché *
            <input
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              placeholder="BAC 240L vert"
            />
          </label>
          <label className="text-xs text-slate-400">
            Code interne
            <input
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              placeholder="Automatique : ATL001, ATL002, ATL003…"
            />
            <span className="mt-1 block text-[10px] text-slate-500">
              Laissez vide pour générer automatiquement le prochain code ATL001, ATL002, ATL003…
            </span>
          </label>
          {form.category === "CONTAINER" || form.category === "CONSUMABLE" || form.category === "PPE" ? (
            <DynamicInput label={form.category === "PPE" ? "Taille / format" : "Volume / format (L)"} value={form.volumeLiters} type="number" onChange={(v) => setForm((f) => ({ ...f, volumeLiters: v }))} />
          ) : null}
          <label className="text-xs text-slate-400">
            Unité
            <input
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={form.unit}
              onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
            />
          </label>
          {["CONTAINER", "CONSUMABLE", "PPE", "TOOL"].includes(form.category) ? (
            <DynamicInput label="Matériau" value={form.material} onChange={(v) => setForm((f) => ({ ...f, material: v }))} />
          ) : null}
          {form.category === "CONTAINER" ? (
            <DynamicInput label="Couleur" value={form.color} onChange={(v) => setForm((f) => ({ ...f, color: v }))} />
          ) : null}
          {["CONTAINER", "ROLLING_EQUIPMENT", "TOOL"].includes(form.category) ? (
            <DynamicInput label="Modèle / format" value={form.model} onChange={(v) => setForm((f) => ({ ...f, model: v }))} />
          ) : null}
          {["ROLLING_EQUIPMENT", "TOOL"].includes(form.category) ? (
            <DynamicInput label="Marque" value={form.brand} onChange={(v) => setForm((f) => ({ ...f, brand: v }))} />
          ) : null}
          {form.category === "ROLLING_EQUIPMENT" ? (
            <>
              <DynamicInput label="Capacité kg" type="number" value={form.capacityKg} onChange={(v) => setForm((f) => ({ ...f, capacityKg: v }))} />
              <DynamicInput label="Capacité m³" type="number" value={form.capacityM3} onChange={(v) => setForm((f) => ({ ...f, capacityM3: v }))} />
              <DynamicInput label="Carburant" value={form.fuelType} onChange={(v) => setForm((f) => ({ ...f, fuelType: v }))} />
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100 md:col-span-3">
                Plaque et châssis seront saisis sur les unités physiques, pas dans le catalogue.
              </div>
            </>
          ) : null}
          <DynamicInput label="Seuil alerte" type="number" value={form.alertThreshold} onChange={(v) => setForm((f) => ({ ...f, alertThreshold: v }))} />
          <div className="flex items-end">
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-500"
            >
              <PlusCircle className="h-4 w-4" />
              {editingId ? "Enregistrer" : "Créer"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="ml-2 rounded-xl border border-slate-700 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800"
              >
                Annuler
              </button>
            ) : null}
          </div>
        </form>
      </section>

      {loading ? (
        <p className="flex items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/40 py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/80 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3">Catégorie</th>
                <th className="px-4 py-3">Famille</th>
                <th className="px-4 py-3">Nature</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Seuil</th>
                  <th className="px-4 py-3">Actif</th>
                  <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-slate-500" colSpan={9}>
                    Aucun article catalogue. Créez d’abord une fiche produit.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-800/80">
                    <td className="px-4 py-3 font-mono text-emerald-200">{r.code}</td>
                    <td className="px-4 py-3 text-slate-200">{r.displayName}</td>
                    <td className="px-4 py-3 text-slate-400">{categoryLabel(r.category)}</td>
                    <td className="px-4 py-3 text-slate-400">{r.family ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-400">{r.nature}</td>
                    <td className="px-4 py-3 text-slate-300">
                      {r.stock?.calculated ?? 0}
                      <span className="text-slate-600"> / rés. </span>
                      {r.stock?.reserved ?? 0}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{r.alertThreshold ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-400">{r.isActive ? "oui" : "non"}</td>
                    <td className="space-x-2 px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => startEdit(r)}
                        className="rounded-lg border border-sky-700/70 px-2 py-1 text-xs text-sky-200 hover:bg-sky-950/30"
                      >
                        <Edit3 className="mr-1 inline h-3 w-3" />
                        Modifier
                      </button>
                      <a
                        href={`/admin/ressources-operationnelles?stockEntryCatalogId=${r.id}`}
                        className="rounded-lg border border-emerald-600/50 px-2 py-1 text-xs text-emerald-200"
                      >
                        <PackagePlus className="mr-1 inline h-3 w-3" /> Entrée stock
                      </a>
                      <button
                        type="button"
                        disabled={!r.isActive}
                        onClick={() => void deactivate(r.id)}
                        className="rounded-lg border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-40"
                      >
                        Désactiver
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(r)}
                        className="rounded-lg border border-rose-700/70 px-2 py-1 text-xs text-rose-200 hover:bg-rose-950/30"
                      >
                        <Trash2 className="mr-1 inline h-3 w-3" />
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ResourceCatalogPage;

function DynamicInput({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label className="text-xs text-slate-400">
      {label}
      <input
        type={type}
        className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
